/**
 * Wie oft eine Leistung im Jahr stattfindet — die eine Stelle im Browser, die zählt.
 *
 * Gegenstück zu `hetzner/webhook/vertrag_frequenz.py`. Seit Migration 152 sind
 * **die Monate die Wahrheit**: `anzahl_pro_jahr` ist in der Datenbank eine
 * abgeleitete Spalte, wird vom Server gar nicht mehr ausgeliefert und darf
 * auch nicht mitgeschickt werden. Wer sie hier noch liest, bekommt
 * `undefined` — und rechnet damit eine Jahressumme, die um Größenordnungen
 * danebenliegt.
 *
 * Deshalb: **immer `einsaetze(position)` aufrufen.**
 */

/** Höchstens zwei Einsätze im selben Monat — Benjamins Entscheidung vom
 *  18.09.2026. Dieselbe Zahl steht in `vertrag_frequenz.MAX_JE_MONAT` und in
 *  der CHECK-Sperre `pflege_monate_gueltig`; laufen sie auseinander, bietet
 *  die Oberfläche etwas an, das die Datenbank ablehnt. */
export const MAX_JE_MONAT = 2;

/** Die Monatsnummern, aufsteigend, MIT Mehrfachnennung: `[3, 3, 5]` heißt
 *  zweimal im März und einmal im Mai. */
export function monate(p: { monate?: number[]; intervall?: string }): number[] {
  if (p.intervall === "monatlich") return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  return [...(p.monate || [])].filter(m => m >= 1 && m <= 12).sort((a, b) => a - b);
}

/** Wie oft im Jahr. Null heißt abgewählt — die Position bleibt stehen und
 *  kostet nichts. */
export function einsaetze(p: { monate?: number[]; intervall?: string }): number {
  return monate(p).length;
}

/** Wie viele Punkte in einem bestimmten Monat liegen. */
export function punkteIn(p: { monate?: number[]; intervall?: string }, monat: number): number {
  return monate(p).filter(m => m === monat).length;
}

/** Ein Klick auf ein Monatsfeld: kein Punkt → einer → zwei → wieder keiner.
 *
 *  Der Rundlauf ist Absicht: wer zu weit geklickt hat, klickt weiter, statt zu
 *  suchen, wie er zurückkommt. Dieselbe Bewegung wie auf der Angebotsseite des
 *  Kunden (`vertrag_frequenz.umschalten`) — das Büro soll dasselbe einstellen
 *  können, was es dem Kunden anbietet. */
export function umschalten(liste: number[] | undefined, monat: number,
                           maxJe: number = MAX_JE_MONAT): number[] {
  const werte = (liste || []).filter(m => m >= 1 && m <= 12);
  const hat = werte.filter(m => m === monat).length;
  const ohne = werte.filter(m => m !== monat);
  const neu = hat >= maxJe ? 0 : hat + 1;
  return [...ohne, ...Array(neu).fill(monat)].sort((a, b) => a - b);
}

// ── Mig 187: wie oft im Monat, und Nebenkosten folgen ihren Leistungen ─────
// Gegenstück zu `vertrag_frequenz.max_je_monat` / `nebenkosten_nachziehen`.
// Der Server rechnet beim Speichern dasselbe noch einmal — hier steht es, damit
// das Büro die Wirkung sieht, bevor gespeichert ist.

type MitKatalog = { leistung_id: string | null };
type KatalogInfo = { id: string; kategorie: string; max_je_monat?: number };

/** Kategorien, die kein eigener Termin sind, sondern bei einem Einsatz anfallen. */
export const NEBENKOSTEN = new Set(["anfahrt", "entsorgung", "maschine"]);

function eintrag(p: MitKatalog, katalog: KatalogInfo[]): KatalogInfo | undefined {
  return p.leistung_id ? katalog.find(k => k.id === p.leistung_id) : undefined;
}

/** 1 oder 2 Punkte je Monat — der Katalog entscheidet (beim Mähen 2). Ohne
 *  Katalogeintrag einer. */
export function maxJeMonat(p: MitKatalog, katalog: KatalogInfo[]): number {
  const n = Number(eintrag(p, katalog)?.max_je_monat || 1);
  return Math.max(1, Math.min(n, MAX_JE_MONAT));
}

export function istNebenkosten(p: MitKatalog, katalog: KatalogInfo[]): boolean {
  return NEBENKOSTEN.has((eintrag(p, katalog)?.kategorie || "").trim().toLowerCase());
}

type Position = MitKatalog & {
  monate: number[]; intervall?: string; optional: boolean; beauftragt: boolean;
  folgt?: string[] | null;
};

/** Die Einsätze als Monatsliste, MIT Mehrfachnennung: je Monat die höchste
 *  Punktzahl einer Leistung, nicht die Summe — zwei Leistungen im selben
 *  Monat sind ein Besuch. `nur` beschränkt auf diese Katalog-IDs. */
export function einsatzMonate(positionen: Position[], katalog: KatalogInfo[],
                              nur?: Set<string>): number[] {
  const je = new Map<number, number>();
  for (const p of positionen) {
    if (istNebenkosten(p, katalog) || (p.optional && !p.beauftragt)) continue;
    if (nur && !nur.has(p.leistung_id || "")) continue;
    for (const m of new Set(monate(p))) je.set(m, Math.max(je.get(m) || 0, punkteIn(p, m)));
  }
  const out: number[] = [];
  for (const [m, n] of [...je.entries()].sort((a, b) => a[0] - b[0])) {
    for (let i = 0; i < n; i++) out.push(m);
  }
  return out;
}

/** Nebenkosten neu rechnen: `folgt` null → alle Einsätze, Liste → nur diese
 *  Leistungen, leer → von Hand. Gibt DIESELBE Liste zurück, wenn sich nichts
 *  ändert — so kann ein Effekt es aufrufen, ohne sich selbst anzustoßen. */
export function nebenkostenNachziehen<T extends Position>(positionen: T[],
                                                        katalog: KatalogInfo[]): T[] {
  let geaendert = false;
  const neu = positionen.map(p => {
    if (!istNebenkosten(p, katalog)) return p;
    const folgt = p.folgt ?? null;
    if (folgt !== null && folgt.length === 0) return p;
    const soll = einsatzMonate(positionen, katalog, folgt ? new Set(folgt) : undefined);
    if (soll.join(",") === monate(p).join(",")) return p;
    geaendert = true;
    return { ...p, monate: soll };
  });
  return geaendert ? neu : positionen;
}
